import _extends from "@babel/runtime/helpers/extends";
import React from 'react';
import styled, { css, keyframes, useTheme } from 'styled-components';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { useFocusRing } from '@react-aria/focus';
import { useHover } from '@react-aria/interactions';
import { mergeProps } from '@react-aria/utils';
import { alto, apple, curiousBlue, dustGray, emperor, java, mercury, mineShaft, white, wildSand } from '@parchment/atoms';
import { rgba } from 'polished';
export function ToggleInternal(_ref) {
  let {
    scale = 'medium',
    context = 'default',
    isTransparent = false,
    ...props
  } = _ref;
  const theme = useTheme();
  const {
    isFocused,
    focusProps: focusRingProps
  } = useFocusRing();
  const {
    isHovered,
    hoverProps
  } = useHover({});
  const inputProps = mergeProps(props.inputProps, focusRingProps);
  return /*#__PURE__*/React.createElement(StyledToggle, _extends({
    className: props.className,
    scale: scale
  }, hoverProps), /*#__PURE__*/React.createElement(VisuallyHidden, null, /*#__PURE__*/React.createElement("input", _extends({}, inputProps, {
    ref: props.inputRef
  }))), /*#__PURE__*/React.createElement(ToggleSlider, {
    context: context,
    theme: theme,
    isTransparent: isTransparent,
    isSelected: props.isSelected,
    isDisabled: props.isDisabled,
    isReadOnly: props.isReadOnly,
    isFocused: isFocused,
    isHovered: isHovered,
    role: "switch",
    "data-testid": "toggle-slider"
  }, toggleText(context, theme, props.isSelected, props.isDisabled, props.isReadOnly, isTransparent, props.onText, props.offText)), /*#__PURE__*/React.createElement(ToggleLabel, null, props.children));
}
const getSize = _ref2 => {
  let {
    scale = 'medium'
  } = _ref2;
  const Sizes = {
    'small': '18px',
    'medium': '24px',
    'large': '30px'
  };
  return css(["font-size:", ";"], Sizes[scale || 'medium']);
};
const getContext = function () {
  let context = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'default';
  let theme = arguments.length > 1 ? arguments[1] : undefined;
  let key = arguments.length > 2 ? arguments[2] : undefined;
  let isSelected = arguments.length > 3 ? arguments[3] : undefined;
  let isDisabled = arguments.length > 4 ? arguments[4] : undefined;
  let isReadOnly = arguments.length > 5 ? arguments[5] : undefined;
  let isTransparent = arguments.length > 6 ? arguments[6] : undefined;
  const visuallyDisabled = isDisabled || isReadOnly;
  const disabledContext = {
    fontColor: dustGray,
    mainColor: isTransparent ? 'transparent' : wildSand,
    borderColor: alto,
    toggleColor: isTransparent ? wildSand : white
  };
  const toggleOffContext = {
    fontColor: isTransparent ? dustGray : white,
    mainColor: isTransparent ? 'transparent' : dustGray,
    borderColor: dustGray,
    toggleColor: isTransparent ? dustGray : mercury
  };
  const toggleContexts = {
    default: {
      fontColor: isTransparent ? emperor : white,
      mainColor: isTransparent ? 'transparent' : emperor,
      borderColor: emperor,
      toggleColor: isTransparent ? emperor : white
    },
    primary: {
      fontColor: isTransparent ? apple : white,
      mainColor: isTransparent ? 'transparent' : apple,
      borderColor: apple,
      toggleColor: isTransparent ? apple : white
    },
    branded: {
      fontColor: isTransparent ? java : white,
      mainColor: isTransparent ? 'transparent' : java,
      borderColor: java,
      toggleColor: isTransparent ? java : white
    },
    configure: {
      fontColor: isTransparent ? curiousBlue : white,
      mainColor: isTransparent ? 'transparent' : curiousBlue,
      borderColor: curiousBlue,
      toggleColor: isTransparent ? curiousBlue : white
    }
  };
  if (visuallyDisabled) {
    return disabledContext[key];
  }
  if (!isSelected) {
    return toggleOffContext[key];
  }
  return toggleContexts[context || 'default'][key];
};
const toggleText = (context, theme, isSelected, isDisabled, isReadOnly, isTransparent, onText, offText) => {
  if (isSelected && onText) {
    return /*#__PURE__*/React.createElement(ToggleOnText, {
      context: context,
      theme: theme,
      isSelected: isSelected,
      isDisabled: isDisabled,
      isReadOnly: isReadOnly,
      isTransparent: isTransparent
    }, onText);
  } else if (!isSelected && offText) {
    return /*#__PURE__*/React.createElement(ToggleOffText, {
      context: context,
      theme: theme,
      isSelected: isSelected,
      isDisabled: isDisabled,
      isReadOnly: isReadOnly,
      isTransparent: isTransparent
    }, offText);
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null);
};
const StyledToggle = /*#__PURE__*/styled.label.withConfig({
  displayName: "ToggleInternal__StyledToggle",
  componentId: "rda6q9-0"
})(["position:relative;display:flex;font-family:\"Open Sans\",Arial,Helvetica,sans-serif;", ""], getSize);
const ToggleSlider = /*#__PURE__*/styled.span.withConfig({
  displayName: "ToggleInternal__ToggleSlider",
  componentId: "rda6q9-1"
})(["position:relative;cursor:", ";height:1.25em;width:2.75em;border-radius:1.25em;border:1px solid ", ";box-shadow:", ";top:0;left:0;right:0;bottom:0;background-color:", ";-webkit-transition:.4s;transition:.4s;transition:outline 0s;&::before{position:absolute;content:\"\";height:.85em;width:.85em;border-radius:50%;left:.2em;bottom:.2em;background-color:", ";-webkit-transition:.4s;transition:.4s;", " ", " ", "}"], _ref3 => {
  let {
    isDisabled,
    isReadOnly
  } = _ref3;
  return isDisabled || isReadOnly ? 'not-allowed' : 'pointer';
}, _ref4 => {
  let {
    context,
    theme,
    isSelected,
    isDisabled,
    isReadOnly,
    isTransparent
  } = _ref4;
  return getContext(context, theme, 'borderColor', isSelected, isDisabled, isReadOnly, isTransparent);
}, _ref5 => {
  let {
    isDisabled,
    isReadOnly,
    isHovered,
    isFocused
  } = _ref5;
  return !isDisabled && (isFocused || isHovered) ? `0 0 6px 2px ${isReadOnly ? rgba(emperor, 0.5) : rgba('#65b6e6', 0.5)}` : 'none';
}, _ref6 => {
  let {
    context,
    theme,
    isSelected,
    isDisabled,
    isReadOnly,
    isTransparent
  } = _ref6;
  return getContext(context, theme, 'mainColor', isSelected, isDisabled, isReadOnly, isTransparent);
}, _ref7 => {
  let {
    context,
    theme,
    isSelected,
    isDisabled,
    isReadOnly,
    isTransparent
  } = _ref7;
  return getContext(context, theme, 'toggleColor', isSelected, isDisabled, isReadOnly, isTransparent);
}, _ref8 => {
  let {
    isSelected
  } = _ref8;
  return isSelected && '-webkit-transform: translateX(1.5em);';
}, _ref9 => {
  let {
    isSelected
  } = _ref9;
  return isSelected && '-ms-transform: translateX(1.5em);';
}, _ref10 => {
  let {
    isSelected
  } = _ref10;
  return isSelected && 'transform: translateX(1.5em);';
});
const ToggleLabel = /*#__PURE__*/styled.div.withConfig({
  displayName: "ToggleInternal__ToggleLabel",
  componentId: "rda6q9-2"
})(["position:relative;margin-left:.5em;padding-top:.2em;height:100%;font-size:.75em;font-style:inherit;color:", ";"], mineShaft);
const fadeIn = /*#__PURE__*/keyframes(["from{opacity:0;}to{opacity:1;}"]);
const ToggleOffText = /*#__PURE__*/styled.span.withConfig({
  displayName: "ToggleInternal__ToggleOffText",
  componentId: "rda6q9-3"
})(["font-size:.5em;font-weight:bold;color:", ";position:absolute;top:0.6em;right:.75em;animation:", " .75s ease-in-out;"], _ref11 => {
  let {
    context,
    theme,
    isSelected,
    isDisabled,
    isReadOnly,
    isTransparent
  } = _ref11;
  return getContext(context, theme, 'fontColor', isSelected, isDisabled, isReadOnly, isTransparent);
}, fadeIn);
const ToggleOnText = /*#__PURE__*/styled.span.withConfig({
  displayName: "ToggleInternal__ToggleOnText",
  componentId: "rda6q9-4"
})(["font-size:.5em;font-weight:bold;color:", ";position:absolute;top:0.6em;left:.75em;animation:", " .75s ease-in-out;"], _ref12 => {
  let {
    context,
    theme,
    isSelected,
    isDisabled,
    isReadOnly,
    isTransparent
  } = _ref12;
  return getContext(context, theme, 'fontColor', isSelected, isDisabled, isReadOnly, isTransparent);
}, fadeIn);